<script lang="ts" setup>
import type {OtherServicesBlock} from '~/types/legacy'
import type {WorkshopOtherServices} from '~/types/workshop'
import type {Populated} from '~/types/populated'
import {resolveMediaUrl} from '~/utils/relations'
import {ref} from 'vue'

const props = defineProps<{
  data: Exclude<Populated<OtherServicesBlock> | Populated<WorkshopOtherServices>, undefined>
}>()

const currentImage = ref<string>(resolveMediaUrl(props.data?.services?.[0]?.image) || '')
const activeServiceIndex = ref<number | null>(null)

const changeActiveImage = (url: string | undefined) => {
  currentImage.value = url || ''
}

const setActiveServiceIndex = (index: number | null) => {
  activeServiceIndex.value = index
}
</script>

<template>
  <Container
    v-if="data"
    class="screen-content group flex flex-col items-center space-y-12 pb-16 pt-32 text-center lg:space-y-16 lg:pb-32 lg:pt-48"
  >
    <div
      :class="{
        'lg:flex lg:justify-between': (data.services?.length ?? 0) > 1,
        'lg:flex lg:items-center lg:justify-center': (data.services?.length ?? 0) === 1,
      }"
      class="w-full space-x-28 lg:items-center"
    >
      <div :class="{'lg:w-1/2 lg:pr-12': (data.services?.length ?? 0) > 1}" class="text-left">
        <Heading as="h2" size="caption" class="text-main-500">
          {{ data.eyebrow }}
        </Heading>
        <Heading as="h3" size="medium" class="mt-2">
          {{ data.title }}
        </Heading>
        <Paragraph class="mt-4 text-gray-600">
          {{ data.body }}
        </Paragraph>

        <div
          v-for="(service, index) in data.services"
          :key="index"
          class="hover-trigger group relative mb-8 first-of-type:mt-16"
          @mouseover="(changeActiveImage(resolveMediaUrl(service.image)), setActiveServiceIndex(index))"
          @mouseleave="setActiveServiceIndex(null)"
        >
          <div class="relative flex items-start">
            <transition name="fade">
              <div
                v-if="activeServiceIndex === index"
                class="absolute -left-4 top-2 h-[85%] w-1 bg-main-500 lg:-left-8"
              ></div>
            </transition>
            <div class="relative">
              <span class="text-xl font-bold text-gray-900">
                {{ service.title }}
              </span>
              <p class="mt-2 text-sm text-gray-600">
                {{ service.body }}
              </p>

              <NuxtLink v-if="activeServiceIndex === index" :to="service.href ?? undefined">
                <span
                  class="group invisible flex items-center py-3 font-bold text-main-600 transition-opacity group-hover:visible group-hover:translate-x-1"
                  style="
                    transition:
                      opacity 0.3s ease,
                      transform 0.3s ease;
                  "
                >
                  {{ $t('workshop.learn_more') }}
                  <Icon name="lucide:move-right" size="18" class="ml-2 transition group-hover:translate-x-1" />
                </span>
              </NuxtLink>
            </div>
          </div>
        </div>
      </div>

      <div :class="{'lg:w-1/2': (data.services?.length ?? 0) > 1}" class="flex items-center justify-center lg:w-full">
        <NuxtImg
          v-if="currentImage"
          :src="currentImage"
          alt="Service Image"
          class="hidden min-h-[448px] min-w-[592px] max-w-md rounded-md border border-gray-200 p-2 shadow-lg lg:block"
        />
      </div>
    </div>
  </Container>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
.fade-enter-to,
.fade-leave-from {
  opacity: 1;
}

.group-hover\:translate-x-1 {
  transform: translateX(0.25rem);
}
.group-hover\:visible {
  visibility: visible;
}
</style>
